import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/CloudUpload";
import {
  IconButton,
  Paper,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {api} from "@core/api";
import {skipToken} from "@reduxjs/toolkit/query";
import {CropDialog} from "./CropDialog";
import LoadingView from "@components/LoadingView";

type Props = {
  name: string;
  fileType: "audio" | "video" | "image" | "document" | "file";
  fileId?: string;
  label?: string;
  errorMsg?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  onChange: (fileId: string | undefined) => void;
};

export default function UploadButton(props: Props) {
  const {name, fileType, label, errorMsg, fileId, disabled, sx, onChange} =
    props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<{name: string; base64: string}>();
  const [accept, setAccept] = useState<string>();
  const [isCropping, setIsCropping] = useState(false);

  const {data, isLoading: isFileLoading} = api.usePreviewFileQuery(
    fileId ?? skipToken,
  );
  const [uploadFile, {isLoading: isUploading}] = api.useUploadFileMutation();
  const isLoading = isFileLoading || isUploading;

  useEffect(() => {
    switch (fileType) {
      case "audio":
        setAccept("audio/*");
        break;
      case "document":
        setAccept(
          ".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,pdf",
        );
        break;
      case "image":
        setAccept("image/*");
        break;
      case "video":
        setAccept("video/*");
        break;
      default:
        setAccept("file");
        break;
    }
  }, [fileType]);

  useEffect(() => {
    if (data) {
      setFile({name: "file", base64: data});
    }
  }, [data]);

  const handleFileLoad = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const fileUpload = evt.target.files?.item(0);
    if (fileUpload) {
      const reader = new FileReader();
      reader.readAsDataURL(fileUpload);
      reader.onload = () => {
        if (inputRef.current) {
          inputRef.current.value = "";
        }

        const base64 = reader.result;
        setFile({name: fileUpload.name, base64: base64 as string});
        if (fileType === "image") {
          setIsCropping(true);
        }
      };
    }
  };

  const handleDelete = () => {
    onChange(undefined);
    setFile(undefined);
  };

  const handleConfirm = async (cropped: File) => {
    setIsCropping(false);
    uploadFile(cropped)
      .unwrap()
      .then(response => {
        onChange(response.data);
      });
  };

  const handleCancel = () => {
    setIsCropping(false);
    onChange(undefined);
    setFile(undefined);
    if (data) {
      setFile({name: "file", base64: data});
    }
  };

  return (
    <Stack direction={"row"} spacing={2} sx={{...sx}}>
      <Stack direction={"column"} justifyContent={"center"}>
        {label && (
          <Typography variant="caption" color="textSecondary">
            {label}
          </Typography>
        )}
        {errorMsg && (
          <Typography variant="caption" color="error">
            {errorMsg}
          </Typography>
        )}
        <Paper sx={{overflow: "hidden"}}>
          <Stack
            alignItems={"center"}
            justifyContent={"center"}
            position={"relative"}>
            <LoadingView loading={isLoading} />
            <img
              src={file?.base64}
              alt=""
              style={{maxWidth: 200, maxHeight: 100}}
            />
          </Stack>
        </Paper>
      </Stack>

      <Stack direction="row" gap={2} pt={file ? 2 : 0} alignItems={"center"}>
        {file && (
          <IconButton
            disabled={disabled || isLoading}
            size="small"
            onClick={handleDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          disabled={disabled || isLoading}
          component="label"
          size="small">
          <UploadIcon fontSize="small" />
          <input
            ref={inputRef}
            name={name}
            hidden
            accept={accept}
            type="file"
            onChange={handleFileLoad}
          />
        </IconButton>
      </Stack>

      <CropDialog
        open={isCropping}
        file={file}
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </Stack>
  );
}
